// src/pages/orderAssignment/components/AssignmentOverviewTable.jsx
import React, { useState } from 'react';
import { Users, RefreshCw, AlertTriangle, CheckSquare, Square, Loader, UserPlus } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './AssignmentOverviewTable.module.css';

/**
 * AssignmentOverviewTable
 *
 * Props:
 *   stats              {Object} — { statuses, users, unassigned }
 *   isLoading          {boolean}
 *   onReassign         {function(selectedUsers: [{userId, userName}])}
 *   onAssignUnassigned {function()}   — called when user clicks "Assign Unassigned Orders"
 *   onRefresh          {function}
 */
const AssignmentOverviewTable = ({ stats, isLoading, onReassign, onAssignUnassigned, onRefresh }) => {
  const { t } = useLocalization();
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  if (isLoading) {
    return (
      <div className={styles.loadingWrap}>
        <Loader size={24} className={styles.spinner} />
        <span>{t('orderAssignment.loadingStats')}</span>
      </div>
    );
  }

  if (!stats) return null;

  const { statuses = [], users = [], unassigned } = stats;

  const hasUnassigned = unassigned && unassigned.total > 0;

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map((u) => u.userId)));
    }
  };

  const handleReassign = () => {
    if (selectedUserIds.size === 0) return;
    const selectedUsers = users
      .filter((u) => selectedUserIds.has(u.userId))
      .map((u) => ({ userId: u.userId, userName: u.userName }));
    onReassign(selectedUsers);
  };

  const allSelected = users.length > 0 && selectedUserIds.size === users.length;
  const someSelected = selectedUserIds.size > 0;

  return (
    <div className={styles.wrap}>
      {/* Section header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Users size={18} className={styles.headerIcon} />
          <div>
            <h2>{t('orderAssignment.assignmentOverview')}</h2>
            <p>{t('orderAssignment.assignmentOverviewInfo')}</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          {someSelected && (
            <button className={styles.reassignBtn} onClick={handleReassign}>
              <RefreshCw size={16} />
              {t('orderAssignment.reassignSelected', { count: selectedUserIds.size })}
            </button>
          )}
          <button
            className={styles.refreshBtn}
            onClick={onRefresh}
            title={t('orderAssignment.refreshStats')}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Unassigned orders banner */}
      {hasUnassigned && (
        <div className={styles.unassignedBanner}>
          <AlertTriangle size={16} />
          <div className={styles.unassignedBannerContent}>
            <div className={styles.unassignedBannerTop}>
              <div>
                <strong>{t('orderAssignment.unassignedOrdersCount')}</strong>
                <span>
                  {' '}—{' '}
                  {t('orderAssignment.unassignedOrdersDescription', { count: unassigned.total })}
                </span>
                {Object.entries(unassigned.byStatus).length > 0 && (
                  <span className={styles.unassignedBreakdown}>
                    {Object.entries(unassigned.byStatus).map(([key, count]) => {
                      const statusInfo = statuses.find((s) => s.key === key);
                      return (
                        <span key={key} className={styles.unassignedChip}>
                          <span
                            className={styles.dot}
                            style={{ backgroundColor: statusInfo?.colorCode || '#6B7280' }}
                          />
                          {statusInfo?.label || key}: {count}
                        </span>
                      );
                    })}
                  </span>
                )}
              </div>
              <button
                className={styles.assignUnassignedBtn}
                onClick={onAssignUnassigned}
                title="Assign these orders to users"
              >
                <UserPlus size={15} />
                {t('orderAssignment.assignUnassignedBtn', {
                  count: unassigned.total,
                  defaultValue: `Assign ${unassigned.total} Unassigned`,
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {users.length === 0 && !hasUnassigned ? (
        <div className={styles.emptyState}>
          <Users size={40} />
          <p>{t('orderAssignment.noAssignmentsYet')}</p>
          <span>{t('orderAssignment.noAssignmentsDescription')}</span>
        </div>
      ) : users.length === 0 ? null : (
        <>
          {/* Table */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkboxCol}>
                    <button className={styles.checkBtn} onClick={toggleAll}>
                      {allSelected ? (
                        <CheckSquare size={17} className={styles.checkActive} />
                      ) : (
                        <Square size={17} className={styles.checkInactive} />
                      )}
                    </button>
                  </th>
                  <th>{t('orderAssignment.user')}</th>
                  <th className={styles.numCol}>{t('orderAssignment.totalAssignedOrders')}</th>
                  {statuses.map((s) => (
                    <th key={s.key} className={styles.statusCol}>
                      <div className={styles.statusHeader}>
                        <span className={styles.dot} style={{ backgroundColor: s.colorCode }} />
                        {s.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelected = selectedUserIds.has(user.userId);
                  return (
                    <tr
                      key={user.userId}
                      className={`${styles.row} ${isSelected ? styles.rowSelected : ''}`}
                      onClick={() => toggleUser(user.userId)}
                    >
                      <td className={styles.checkboxCol} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.checkBtn} onClick={() => toggleUser(user.userId)}>
                          {isSelected ? (
                            <CheckSquare size={17} className={styles.checkActive} />
                          ) : (
                            <Square size={17} className={styles.checkInactive} />
                          )}
                        </button>
                      </td>
                      <td>
                        <div className={styles.userName}>{user.userName}</div>
                      </td>
                      <td className={styles.numCol}>
                        <span className={styles.totalBadge}>{user.total}</span>
                      </td>
                      {statuses.map((s) => {
                        const count = user.byStatus[s.key] || 0;
                        return (
                          <td key={s.key} className={styles.statusCol}>
                            {count > 0 ? (
                              <span
                                className={styles.countBadge}
                                style={{
                                  backgroundColor: `${s.colorCode}18`,
                                  color: s.colorCode,
                                  borderColor: `${s.colorCode}40`,
                                }}
                              >
                                {count}
                              </span>
                            ) : (
                              <span className={styles.zeroCount}>—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Selection hint */}
          {someSelected && (
            <div className={styles.selectionHint}>
              {selectedUserIds.size} user(s) selected —{' '}
              <button className={styles.clearSel} onClick={() => setSelectedUserIds(new Set())}>
                Clear selection
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssignmentOverviewTable;